import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ScanLine, ShieldCheck, ShieldAlert, ShieldX, ArrowRight, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { Seo } from "@/components/web/Seo";
import { MaskedLines, FadeUp } from "@/components/web/Reveal";
import { scanUrl } from "@/lib/scanApi";

const normalizeUrl = (value) => (value.match(/^https?:\/\//i) ? value : `https://${value}`);

const isValidUrl = (value) => {
  try {
    const u = new URL(normalizeUrl(value));
    return (u.protocol === "http:" || u.protocol === "https:") && u.hostname.includes(".");
  } catch {
    return false;
  }
};

const SCAN_STEPS = [
  "RESOLVING DOMAIN…",
  "TRACING REDIRECT CHAIN…",
  "CHECKING THREAT INTELLIGENCE…",
  "RUNNING AI PHISHING ANALYSIS…",
];

const VERDICTS = {
  safe: {
    label: "SAFE",
    Icon: ShieldCheck,
    text: "text-[#22C55E]",
    border: "border-[#22C55E]/40",
    bg: "bg-[#22C55E]/5",
    glow: "shadow-[0_0_24px_rgba(34,197,94,0.15)]",
  },
  suspicious: {
    label: "SUSPICIOUS",
    Icon: ShieldAlert,
    text: "text-[#FACC15]",
    border: "border-[#FACC15]/40",
    bg: "bg-[#FACC15]/5",
    glow: "shadow-[0_0_24px_rgba(250,204,21,0.15)]",
  },
  dangerous: {
    label: "DANGEROUS",
    Icon: ShieldX,
    text: "text-[#FF3366]",
    border: "border-[#FF3366]/50",
    bg: "bg-[#FF3366]/5",
    glow: "shadow-[0_0_24px_rgba(255,51,102,0.2)]",
  },
};

const errorMessage = (err) => {
  if (err.status === 400) return "We couldn't validate that URL. Please check the link and try again.";
  if (err.status === 429) {
    const wait = err.retryAfter ? ` Please try again in about ${err.retryAfter} seconds.` : " Please try again shortly.";
    return `Too many scans right now.${wait}`;
  }
  if (err.status === 503) return "The scanner is temporarily unavailable. Please try again shortly.";
  if (err.status) return "The scan could not be completed. Please try again shortly.";
  return "Connection problem — we couldn't reach the scanner. Please check your internet and try again.";
};

export default function Scanner() {
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | scanning | result
  const [stepIdx, setStepIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [showTech, setShowTech] = useState(false);

  const reset = () => {
    setPhase("idle");
    setUrl("");
    setResult(null);
    setError("");
    setShowTech(false);
  };

  const handleScan = async (e) => {
    e.preventDefault();
    const value = url.trim();
    if (!value || !isValidUrl(value)) {
      setError("Please enter a valid URL (e.g. https://example.com).");
      return;
    }
    const target = normalizeUrl(value);
    setError("");
    setResult(null);
    setShowTech(false);
    setPhase("scanning");
    setStepIdx(0);
    SCAN_STEPS.forEach((_, i) => setTimeout(() => setStepIdx(i), i * 550));
    const minDelay = new Promise((r) => setTimeout(r, SCAN_STEPS.length * 550 + 300));
    try {
      const [data] = await Promise.all([scanUrl(target), minDelay]);
      setResult(data);
      setPhase("result");
    } catch (err) {
      setError(errorMessage(err));
      setPhase("idle");
    }
  };

  const v = result ? VERDICTS[result.verdict] || VERDICTS.suspicious : null;

  return (
    <>
      <Seo
        title="Free URL Scanner — Check if a Link Is Safe | LinkExpln"
        description="Paste any link and LinkExpln instantly analyzes it for phishing, scams and malware using live threat intelligence. Free, no sign-up needed."
      />

      <section className="pt-24 pb-16 sm:pt-32 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="ls-overline mb-6">The Scanner</p>
          <h1
            data-testid="scanner-heading"
            className="font-heading font-black tracking-tighter text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.95]"
          >
            <MaskedLines lines={["CHECK BEFORE", "YOU CLICK."]} />
          </h1>
          <p className="mt-8 max-w-xl text-zinc-400 text-base sm:text-lg leading-relaxed">
            Paste any link below. LinkExpln will analyze it for phishing,
            scam and malware signals.
          </p>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <div className="border border-white/10 bg-[#0A121D]">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-white/10">
                <span className="h-2.5 w-2.5 rounded-full bg-[#FF3366]" />
                <span className="h-2.5 w-2.5 rounded-full bg-zinc-600" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#00C5FE]" />
                <span className="ml-3 font-mono-ls text-[10px] tracking-[0.2em] text-zinc-400 uppercase">
                  linkexpln://scan
                </span>
              </div>

              <div className="p-6 sm:p-10">
                <form onSubmit={handleScan} className="flex flex-col sm:flex-row gap-3">
                  <label htmlFor="scanner-url" className="sr-only">URL to scan</label>
                  <input
                    id="scanner-url"
                    data-testid="scanner-input"
                    type="text"
                    inputMode="url"
                    placeholder="https://suspicious-link.example"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    disabled={phase === "scanning"}
                    className="flex-1 bg-black border border-white/15 px-5 py-4 font-mono-ls text-sm text-white placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#00C5FE] focus:border-transparent"
                  />
                  <button
                    type="submit"
                    data-testid="scanner-submit-button"
                    disabled={phase === "scanning"}
                    className="inline-flex items-center justify-center gap-2 btn-brand disabled:opacity-50 text-black font-heading font-bold px-8 py-4 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#00C5FE] focus:ring-offset-2 focus:ring-offset-black"
                  >
                    <ScanLine size={18} strokeWidth={2.5} />
                    {phase === "scanning" ? "Scanning link…" : "Scan Link"}
                  </button>
                </form>
                {error && (
                  <p data-testid="scanner-error" className="mt-4 font-mono-ls text-xs text-[#FF3366]" role="alert">
                    {error}
                  </p>
                )}

                <AnimatePresence mode="wait">
                  {phase === "scanning" && (
                    <motion.div
                      key="scanning"
                      data-testid="scanner-scanning-state"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="mt-8 relative border border-white/10 bg-black p-6 overflow-hidden"
                    >
                      <div className="ls-scanline absolute left-0 right-0 h-px bg-[#00C5FE]/70 shadow-[0_0_12px_#00C5FE]" />
                      <p className="font-mono-ls text-xs tracking-[0.15em] text-zinc-400 mb-3 break-all">TARGET: {normalizeUrl(url.trim())}</p>
                      {SCAN_STEPS.slice(0, stepIdx + 1).map((s, i) => (
                        <p key={s} className="font-mono-ls text-xs sm:text-sm text-[#00C5FE] leading-loose">
                          <span className="text-zinc-600 mr-2">[{String(i + 1).padStart(2, "0")}]</span>
                          {s}
                          {i === stepIdx && <span className="ls-blink ml-1">▊</span>}
                        </p>
                      ))}
                    </motion.div>
                  )}

                  {phase === "result" && result && v && (
                    <motion.div
                      key="result"
                      data-testid="scanner-result"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-8 border ${v.border} ${v.bg} ${v.glow}`}
                    >
                      <div className="p-6 sm:p-8">
                        <div className="flex flex-wrap items-center gap-4">
                          <v.Icon size={36} className={`${v.text} shrink-0`} strokeWidth={2} />
                          <div className="min-w-0">
                            <p data-testid="scanner-verdict" className={`font-heading font-black tracking-tight text-2xl sm:text-3xl ${v.text}`}>
                              {v.label}
                            </p>
                            <p data-testid="scanner-result-url" className="font-mono-ls text-[11px] tracking-[0.1em] text-zinc-400 mt-1 break-all">
                              {result.domain || result.normalized_url}
                            </p>
                          </div>
                        </div>

                        <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4 max-w-sm">
                          <div className="border border-white/10 bg-black/40 px-4 py-3">
                            <p className="font-mono-ls text-[9px] tracking-[0.2em] uppercase text-zinc-500">Risk score</p>
                            <p data-testid="scanner-risk-score" className={`font-heading font-bold text-xl ${v.text}`}>{result.risk_score}<span className="text-zinc-500 text-sm font-normal">/100</span></p>
                          </div>
                          <div className="border border-white/10 bg-black/40 px-4 py-3">
                            <p className="font-mono-ls text-[9px] tracking-[0.2em] uppercase text-zinc-500">Confidence</p>
                            <p data-testid="scanner-confidence" className="font-heading font-bold text-xl text-white">{result.confidence}<span className="text-zinc-500 text-sm font-normal">%</span></p>
                          </div>
                        </div>

                        {Array.isArray(result.categories) && result.categories.filter((c) => c && c !== "unknown").length > 0 && (
                          <div data-testid="scanner-categories" className="mt-5 flex flex-wrap gap-2">
                            {result.categories.filter((c) => c && c !== "unknown").map((c) => (
                              <span key={c} className={`font-mono-ls text-[10px] tracking-[0.15em] uppercase border ${v.border} ${v.text} px-3 py-1`}>
                                {c}
                              </span>
                            ))}
                          </div>
                        )}

                        {result.summary && (
                          <p data-testid="scanner-summary" className="mt-6 text-zinc-300 text-sm leading-relaxed">
                            {result.summary}
                          </p>
                        )}

                        {Array.isArray(result.detected_issues) && result.detected_issues.length > 0 && (
                          <div data-testid="scanner-issues" className="mt-6">
                            <p className="font-mono-ls text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Detected issues</p>
                            <ul className="space-y-2">
                              {result.detected_issues.map((issue, i) => (
                                <li key={i} className={`flex gap-2 text-sm leading-relaxed ${v.text}`}>
                                  <span className="shrink-0 mt-0.5">⚠</span>
                                  <span className="text-zinc-300">{typeof issue === "string" ? issue : issue.description || issue.title || JSON.stringify(issue)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {Array.isArray(result.recommendations) && result.recommendations.length > 0 && (
                          <div data-testid="scanner-recommendations" className="mt-6">
                            <p className="font-mono-ls text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Recommendations</p>
                            <ul className="space-y-2">
                              {result.recommendations.map((r, i) => (
                                <li key={i} className="flex gap-2 text-zinc-400 text-sm leading-relaxed">
                                  <span className="text-[#00C5FE] shrink-0">—</span>
                                  {r}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {result.technical_details && (
                          <div className="mt-6 border-t border-white/10 pt-4">
                            <button
                              type="button"
                              data-testid="scanner-tech-toggle"
                              onClick={() => setShowTech((s) => !s)}
                              className="inline-flex items-center gap-2 font-mono-ls text-[11px] tracking-[0.15em] uppercase text-zinc-400 hover:text-white transition-colors duration-200"
                            >
                              <ChevronDown size={14} className={`transition-transform duration-200 ${showTech ? "rotate-180" : ""}`} />
                              Technical details
                            </button>
                            {showTech && (
                              <pre data-testid="scanner-tech-details" className="mt-3 bg-black border border-white/10 p-4 font-mono-ls text-[11px] text-zinc-400 leading-relaxed overflow-x-auto max-h-72 overflow-y-auto whitespace-pre-wrap break-all">
{JSON.stringify(result.technical_details, null, 2)}
                              </pre>
                            )}
                          </div>
                        )}

                        <button
                          data-testid="scanner-reset-button"
                          onClick={reset}
                          className="mt-7 font-mono-ls text-xs tracking-[0.15em] uppercase text-[#00C5FE] hover:text-white transition-colors duration-200 underline underline-offset-4"
                        >
                          Scan another link
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.15}>
            <div className="mt-12 border border-white/10 p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="ls-overline mb-4">Think a link is dangerous?</p>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  Flag it for our team so we can protect other users too.
                </p>
                <Link
                  to="/report"
                  data-testid="scanner-report-link"
                  className="mt-4 inline-flex items-center gap-2 text-white hover:text-[#00C5FE] font-heading font-semibold text-sm transition-colors duration-200"
                >
                  Report a suspicious link <ArrowRight size={15} />
                </Link>
              </div>
              <div>
                <p className="ls-overline mb-4">Got an APK file to check?</p>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  Inspect an Android app's identity, certificate and permissions before installing.
                </p>
                <Link
                  to="/apk-checker"
                  data-testid="scanner-apk-link"
                  className="mt-4 inline-flex items-center gap-2 text-white hover:text-[#00C5FE] font-heading font-semibold text-sm transition-colors duration-200"
                >
                  Open the APK Checker <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
